import { useEffect, useRef, useState } from "react";
import { Send, X } from "lucide-react";
import YumoPotIcon from "@/components/YumoPotIcon";
import RecipeCard from "@/components/RecipeCard";
import RestaurantResults from "@/components/RestaurantResults";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

/**
 * Floating "Ask YUMO" entry point. Visible to authenticated users on every page.
 * - Bottom-right floating button on desktop; bottom-right corner with safe-area on mobile.
 * - Opens a right-side Sheet (Shadcn drawer) — does NOT alter Layout or Navbar.
 * - Conversation persisted client-side per session (sessionStorage).
 */
const STARTER_PROMPTS = [
  "Romantic dinner nearby",
  "Best biryani within 5 km",
  "Spicy Andhra food, quiet place",
  "I have chicken, onion, garlic",
];

// Detect ingredient-list intent (2+ comma-separated tokens OR explicit prefix)
function isRecipeIntent(text) {
  const t = text.toLowerCase().trim();
  if (/^(recipe:|i have|recipe from|make.*with)/i.test(t)) return true;
  const parts = t.split(/[,+]/).map(s => s.trim()).filter(Boolean);
  return parts.length >= 2 && parts.every(p => p.length < 30 && !/[.?!]/.test(p));
}

// Detect restaurant-discovery intent (must be selective — reject "breakfast in Mysore" which is an ai/ask query)
const RESTAURANT_TRIGGERS = /(restaurant|place to eat|dine\b|rooftop|near me\b|nearby\b|within \d+\s?km|romantic\s+(dinner|lunch|place)|find me\s+.+(place|spot|joint)|best\s+\w+\s+(near|within)\s+(me|\d)|café near|cafe near)/i;
function isRestaurantIntent(text) {
  if (isRecipeIntent(text)) return false; // ingredient list wins
  return RESTAURANT_TRIGGERS.test(text);
}

async function getLocationOnce() {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 6000, maximumAge: 5 * 60 * 1000, enableHighAccuracy: false }
    );
  });
}

export default function AskYumo() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // [{role:'user'|'yumo', text, provider?}]
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [lastRestaurantIntent, setLastRestaurantIntent] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const stored = sessionStorage.getItem("yumo_ask_msgs");
    const sid = sessionStorage.getItem("yumo_ask_session");
    if (stored) {
      try { setMessages(JSON.parse(stored)); } catch (e) { console.warn(e?.message); }
    }
    if (sid) setSessionId(sid);
  }, [user]);

  useEffect(() => {
    if (messages.length) sessionStorage.setItem("yumo_ask_msgs", JSON.stringify(messages.slice(-20)));
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!user) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      if (isRestaurantIntent(msg)) {
        const loc = await getLocationOnce();
        const { data } = await api.post("/restaurants/discover", {
          query: msg,
          lat: loc?.lat,
          lon: loc?.lon,
          session_id: sessionId,
          previous_intent: lastRestaurantIntent,
        });
        setLastRestaurantIntent(data.intent || null);
        if (data.clarify && (!data.results || data.results.length === 0)) {
          setMessages((m) => [...m, { role: "yumo", text: data.clarify }]);
        } else {
          setMessages((m) => [...m, { role: "yumo", kind: "restaurants", payload: data, intent: data.intent }]);
        }
      } else if (isRecipeIntent(msg)) {
        const ingredients = msg.replace(/^(recipe:|i have|recipe from|make.*with)/i, "").trim().split(/[,+]/).map(s => s.trim()).filter(Boolean);
        const { data } = await api.post("/ai/generate-recipe", { ingredients });
        setMessages((m) => [...m, { role: "yumo", kind: "recipe", recipe: data.recipe, source: data.source, provider: data.provider }]);
      } else {
        const { data } = await api.post("/ai/ask", { message: msg, session_id: sessionId });
        if (data.session_id && !sessionId) {
          setSessionId(data.session_id);
          sessionStorage.setItem("yumo_ask_session", data.session_id);
        }
        setMessages((m) => [...m, { role: "yumo", text: data.text, provider: data.provider, fallback: data.fallback_used }]);
      }
    } catch (err) {
      setMessages((m) => [...m, { role: "yumo", text: formatApiError(err.response?.data?.detail) || "I couldn't reach the kitchen brain. Try again in a moment." }]);
    } finally {
      setSending(false);
    }
  };

  const clear = () => {
    setMessages([]);
    setSessionId(null);
    setLastRestaurantIntent(null);
    sessionStorage.removeItem("yumo_ask_msgs");
    sessionStorage.removeItem("yumo_ask_session");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          aria-label="Open YumoPot"
          data-testid="ask-yumo-fab"
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 active:scale-95 transition-transform flex items-center justify-center yumo-pulse"
          style={{ boxShadow: "0 0 24px rgba(233, 122, 47, 0.35)" }}
        >
          <YumoPotIcon className="w-7 h-7" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col p-0 gap-0"
        data-testid="ask-yumo-drawer"
      >
        <SheetHeader className="p-5 border-b border-border">
          <SheetTitle className="font-serif text-2xl tracking-tight flex items-center gap-2">
            <YumoPotIcon className="w-6 h-6 text-primary" />
            YumoPot
          </SheetTitle>
          <div className="text-xs text-muted-foreground">Your food brain. Ask anything — or drop ingredients for a recipe.</div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ask-yumo-messages">
          {messages.length === 0 && (
            <div className="space-y-3">
              <div className="overline">Try asking</div>
              {STARTER_PROMPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => send(p)}
                  className="w-full text-left text-sm p-3 rounded-xl bg-secondary/60 hover:bg-secondary transition border border-border"
                  data-testid="ask-yumo-starter"
                >
                  {p}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={`${m.role}-${i}`}
              className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
            >
              {m.kind === "recipe" ? (
                <RecipeCard recipe={m.recipe} source={m.source} provider={m.provider} />
              ) : m.kind === "restaurants" ? (
                <RestaurantResults payload={m.payload} intent={m.intent} />
              ) : (
                <div
                  className={
                    m.role === "user"
                      ? "max-w-[85%] bg-primary text-primary-foreground rounded-2xl rounded-br-md px-4 py-2.5 text-sm leading-snug"
                      : "max-w-[90%] bg-secondary/60 rounded-2xl rounded-bl-md px-4 py-3 text-sm leading-relaxed border border-border"
                  }
                  data-testid={`ask-yumo-msg-${m.role}`}
                >
                  <div className="whitespace-pre-wrap">{m.text}</div>
                  {m.role === "yumo" && m.provider && (
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                      via {m.provider}{m.fallback ? " (fallback)" : ""}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-secondary/60 border border-border rounded-2xl px-4 py-3 text-sm text-muted-foreground">
                YUMO is thinking…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-border p-3 flex gap-2 items-center bg-background">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask anything about food…"
            disabled={sending}
            data-testid="ask-yumo-input"
            className="flex-1 bg-secondary/40 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
          />
          <Button
            size="icon"
            onClick={() => send()}
            disabled={sending || !input.trim()}
            data-testid="ask-yumo-send"
            className="rounded-full shrink-0"
            aria-label="Send"
          >
            <Send className="w-4 h-4" strokeWidth={1.75} />
          </Button>
          {messages.length > 0 && (
            <button
              onClick={clear}
              data-testid="ask-yumo-clear"
              aria-label="Clear conversation"
              className="rounded-full p-2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" strokeWidth={1.75} />
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
